import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const skills = [
  { name: "Android (Java/Kotlin)", level: 95 },
  { name: "iOS (Swift)", level: 85 },
  { name: "Firebase", level: 90 },
  { name: "UI/UX Design", level: 88 },
  { name: "Figma", level: 92 },
  { name: "HTML/CSS", level: 85 },
  { name: "JavaScript", level: 80 },
  { name: "WordPress", level: 75 },
];

export const Skills = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <section
      id="skills"
      ref={ref}
      className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24 bg-[#0C0C0C]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-4">
              Technical Expertise
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              Skills & Technologies
            </h2>
          </motion.div>

          {/* Skills Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {skills.map((skill, index) => (
              <motion.div
                key={skill.name}
                variants={itemVariants}
                className="bg-[#050505] border border-white/10 p-6 hover:border-[#D4FF00]/50 transition-all duration-300 group"
              >
                <div className="flex justify-between items-center mb-3">
                  <span className="text-white font-medium group-hover:text-[#D4FF00] transition-colors">
                    {skill.name}
                  </span>
                  <span className="font-mono text-sm text-[#888888]">
                    {skill.level}%
                  </span>
                </div>
                <div className="h-1 bg-white/10 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={isInView ? { width: `${skill.level}%` } : { width: 0 }}
                    transition={{ duration: 1, delay: index * 0.1, ease: "easeOut" }}
                    className="h-full bg-[#D4FF00]"
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tech Stack Tags */}
          <motion.div variants={itemVariants} className="mt-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#888888] mb-6">
              Tools & Frameworks
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                "Android Studio",
                "Xcode",
                "Firebase",
                "Figma",
                "Git",
                "REST APIs",
                "SQLite",
                "Room Database",
                "MVVM",
                "Jetpack Compose",
              ].map((tech) => (
                <span
                  key={tech}
                  className="px-4 py-2 bg-white/5 border border-white/10 text-[#888888] text-sm font-mono hover:border-[#D4FF00] hover:text-[#D4FF00] transition-all duration-300 cursor-default"
                >
                  {tech}
                </span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
